import _ from "lodash";
// Internal
import {
  canOverrideScoreInReports,
  isNewDecimalSettingEnabled,
  getIsGradingHistoryEnhancementsEnabledInReports,
  getHidePercentageInFinalScoreInReports,
} from "../featureFlags";
import { getOverallRating, getShowAsteriskOnRating } from "../ratingPrimitives";

// DOC -  https://coda.io/d/_dyjgYFbm_Bg/Final-score_sutRW#_lu7Y1
export const getFinalScoreCriteriaSet = ({
  ratings,
  criteriaSet,
  subjectId,
  globalCategoryId,
  termId,
  gradingPeriodType,
  termFinalScoreDisplayType,
  childGradingPeriod,
  curriculumType,
  subjectType,
  academicCriteriaSets,
  isScoreAggregationEnabled,
  isScoreAndGradeAggregationEnabled,
  scoreAggregationConfiguration,
  termCriteriaAsteriskMap,
  isNewGradeScaleSettingsEnabled = false,
}) => {
  // Get final score differently based on permission
  const {
    finalScore,
    calculatedGrade,
    showWarning,
  } = getFinalScoreBasedOnPermission({
    ratings,
    subjectId,
    globalCategoryId,
    curriculumType,
    subjectType,
    academicCriteriaSets,
    criteriaSet,
    termId,
    gradingPeriodType,
  });

  const finalScoreValue = _.get(finalScore, "value", null);
  const updatedFinalScoreValue = getUpdatedFinalScoreValue({
    scoreValue: finalScoreValue,
    gradingPeriodType,
    termFinalScoreDisplayType,
    childGradingPeriod,
  });

  const updatedCalculatedFinalScore = getUpdatedFinalScoreValue({
    scoreValue: calculatedGrade,
    gradingPeriodType,
    termFinalScoreDisplayType,
    childGradingPeriod,
  });

  const isGradingHistoryEnhancementsEnabledInReports = getIsGradingHistoryEnhancementsEnabledInReports();

  const label =
    isGradingHistoryEnhancementsEnabledInReports ||
    getHidePercentageInFinalScoreInReports()
      ? criteriaSet.label
      : `${criteriaSet.label} (100%)`;

  // to handle grade scales for new grading setup map is created based on id and not criteriaType as criteriaType is not defined for grade scales
  const asteriskKey = isNewGradeScaleSettingsEnabled
    ? criteriaSet.id
    : criteriaSet.criteriaType;
  // For now score aggregation is enabled in cumulative report section view only
  const showAsteriskOnRating = getShowAsteriskOnRating({
    isScoreAggregationEnabled,
    gradingPeriodType,
    isScoreAndGradeAggregationEnabled,
    scoreAggregationConfiguration,
    subjectId,
    academicCriteriaSetId: criteriaSet.id,
    academicTermId: termId,
    showAsterisk: _.get(termCriteriaAsteriskMap, [termId, asteriskKey], false),
    isNewGradeScaleSettingsEnabled,
    criteriaType: criteriaSet.criteriaType,
  });

  return {
    ...criteriaSet,
    label,
    value: updatedFinalScoreValue,
    showWarning,
    rawFinalScore: finalScoreValue,
    displayFinalScore: updatedFinalScoreValue,
    rawCalculatedFinalScore: calculatedGrade,
    displayCalculatedFinalScore: updatedCalculatedFinalScore,
    rawFinalScoreWeightage: 100,
    displayFinalScoreWeightage: "100%",
    showAsteriskOnRating,
  };
};

const getFinalScoreBasedOnPermission = ({
  ratings,
  subjectId,
  globalCategoryId,
  curriculumType,
  subjectType,
  academicCriteriaSets,
  criteriaSet,
  termId,
  gradingPeriodType,
}) => {
  let finalScore;
  let calculatedGrade = null;
  let showWarning = false;

  if (canOverrideScoreInReports()) {
    // Use edited final score if it exist
    // Else use computed final score
    const overallRating = getOverallRating({
      overallGradeSet: criteriaSet,
      ratings,
      subjectId,
      globalCategoryId,
      curriculumType,
      subjectType,
      academicCriteriaSets,
      criteriaSet,
      gradingPeriodType,
      termId,
    });

    finalScore = _.get(overallRating, "rating", null);
    calculatedGrade = _.get(overallRating, "calculatedGrade", null);
    showWarning = _.get(overallRating, "showWarning", false);
  } else {
    // Search based on criteria set and subject
    // Also if term is available we will add term check
    const ratingSearchCriteria = {
      academicCriteriaSet: criteriaSet.id,
      ibPYPElementId: subjectId,
      ...(!_.isEmpty(termId) && { academicTermId: termId.toString() }),
    };

    // Get score from ratings
    finalScore = _.find(ratings, ratingSearchCriteria);
  }

  return { finalScore, calculatedGrade, showWarning };
};

const getUpdatedFinalScoreValue = ({
  scoreValue,
  gradingPeriodType,
  termFinalScoreDisplayType,
  childGradingPeriod,
}) => {
  if (
    _.isNil(scoreValue) ||
    scoreValue == "-" ||
    (_.isString(scoreValue) && _.isEmpty(scoreValue))
  ) {
    return "-";
  }

  let roundedValue = scoreValue;

  // Previous locked reports will work as before
  if (!isNewDecimalSettingEnabled()) {
    roundedValue = _.round(scoreValue, 2);
  }

  const shouldAddPercentageSuffix = shouldAddPercentageSuffixInFinalScore({
    gradingPeriodType,
    termFinalScoreDisplayType,
    childGradingPeriod,
  });

  return shouldAddPercentageSuffix
    ? `${_.toString(roundedValue)}%`
    : _.toString(roundedValue);
};

const shouldAddPercentageSuffixInFinalScore = ({
  gradingPeriodType,
  termFinalScoreDisplayType,
  childGradingPeriod,
}) => {
  // In term report final score will always be in %
  // In cumulative report we add % based on termFinalScoreDisplayType setting and term/childGradingPeriod type
  // If termFinalScoreDisplayType = "WEIGHT" and term/childGradingPeriod = "REPORTING", then remove %
  const shouldHide =
    (gradingPeriodType == "CUMULATIVE" &&
      termFinalScoreDisplayType == "WEIGHT" &&
      childGradingPeriod == "REPORTING") ||
    getHidePercentageInFinalScoreInReports();
  return !shouldHide;
};
